import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productListCategoryFiltersStyles } from "@insite/content-library/Widgets/ProductList/ProductListCategoryFilters";
import ProductListFiltersAccordionSection, {
    ProductListFilterAccordionSectionStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import Link, { LinkProps } from "@insite/mobius/Link";
import { color } from "@stoneage/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    expandByDefault = "expandByDefault",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.expandByDefault]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    // xng-change: take show more limit from settings
    facetsValuesShowMoreLimit: (getSettingsCollection(state).customSettings as any).plpFacetsValuesShowMoreLimit || 10,
    facetsTypesShowMoreLimit: (getSettingsCollection(state).customSettings as any).plpFacetsTypesShowMoreLimit || 10,
    attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
});

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

const linksStyle: LinkProps = {
    color: "secondary",
    typographyProps: {
        size: 12,
        weight: "bold",
        css: css`
            &&& :hover {
                color: ${color("primary")};
            }
        `,
    },
    css: css`
        padding-top: 4px;
    `,
};

export const productListAttributeFiltersStyles: ProductListFilterAccordionSectionStyles = {
    seeAllLink: linksStyle,
    seeLessLink: linksStyle,
};

const ProductListAttributeFilters: FC<Props> = ({
    attributeTypeFacets,
    facetsValuesShowMoreLimit,
    facetsTypesShowMoreLimit,
    addProductFilters,
    removeProductFilters,
    fields,
}) => {
    if (!attributeTypeFacets?.length) {
        return null;
    }

    const [expanded, setExpanded] = React.useState(false);

    const limitedTypeFacets = expanded ? attributeTypeFacets : attributeTypeFacets?.slice(0, facetsTypesShowMoreLimit);

    const onChangeFacet = (facet: FacetModel) => {
        if (facet.selected) {
            removeProductFilters({ attributeValueIds: [facet.id] });
        } else {
            addProductFilters({ attributeValueIds: [facet.id] });
        }
    };

    const styles = productListAttributeFiltersStyles;

    return (
        <>
            {limitedTypeFacets.map(attrType =>
                attrType.attributeValueFacets ? (
                    <ProductListFiltersAccordionSection
                        key={attrType.attributeTypeId}
                        title={attrType.nameDisplay}
                        facets={attrType.attributeValueFacets.map<FacetModel>(av => ({
                            ...av,
                            id: av.attributeValueId,
                            name: av.valueDisplay,
                        }))}
                        onChangeFacet={onChangeFacet}
                        showMoreLimit={facetsValuesShowMoreLimit}
                        expandByDefault={fields.expandByDefault}
                        extendedStyles={productListCategoryFiltersStyles}
                    />
                ) : null,
            )}
            {attributeTypeFacets &&
                attributeTypeFacets.length > facetsTypesShowMoreLimit &&
                (expanded ? (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(false)}>
                        {translate("Load Less -")}
                    </Link>
                ) : (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                        {translate("Load More +")}
                    </Link>
                ))}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListAttributeFilters),
    definition: {
        group: "Product List",
        displayName: "Attribute Filters",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
